import { useEffect, useMemo, useState } from "react";
import ConfirmModal from "./ConfirmModal";

const SECTION_DEFINITIONS = [
  {
    key: "hero",
    label: "Hero",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    description: "Manage the hero copy, CTA buttons, and homepage carousel slides.",
    sectionFields: [
      { name: "badge", label: "Badge", type: "text", placeholder: "Now Enrolling - 2026 Academic Year" },
      { name: "title", label: "Headline", type: "text", placeholder: "Start Your Creative Journey Today" },
      { name: "subtitle", label: "Subheading", type: "textarea", rows: 3, placeholder: "Short hero description" },
      { name: "button_text", label: "Primary Button Text", type: "text", placeholder: "Apply Now" },
      { name: "button_link", label: "Primary Button Link", type: "text", placeholder: "/apply" },
      { name: "secondary_button_text", label: "Secondary Button Text", type: "text", placeholder: "Explore Courses" },
      { name: "secondary_button_link", label: "Secondary Button Link", type: "text", placeholder: "/courses" },
      { name: "is_enabled", label: "Show Hero Section", type: "toggle" },
    ],
    itemSection: "hero_slides",
    itemLabel: "Slide",
    itemDescription: "Slides are displayed in ascending order.",
    itemFields: [
      { name: "title", label: "Slide Title", type: "text", placeholder: "Slide title" },
      { name: "subtitle", label: "Slide Subtitle", type: "textarea", rows: 3, placeholder: "Slide subtitle" },
      { name: "button_text", label: "Button Text", type: "text", placeholder: "Apply Now" },
      { name: "button_link", label: "Button Link", type: "text", placeholder: "/apply" },
      { name: "sort_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_enabled", label: "Show Slide", type: "toggle" },
    ],
    itemImageField: { name: "image", label: "Carousel Image", required: true },
  },
  {
    key: "about",
    label: "About Us",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    description: "Control the About Us section, including text, vision, mission, images, and highlights.",
    sectionFields: [
      { name: "title", label: "Section Title", type: "text", placeholder: "About Mokorotlo College of Creative Technology" },
      { name: "subtitle", label: "Section Subtitle", type: "textarea", rows: 3, placeholder: "Short intro for the section" },
      { name: "badge", label: "Badge / Eyebrow", type: "text", placeholder: "Background, Vision, Mission, and Highlights" },
      { name: "body", label: "Primary Description", type: "textarea", rows: 5, placeholder: "Main About Us content" },
      { name: "secondary_body", label: "Secondary Description", type: "textarea", rows: 5, placeholder: "Additional About Us content" },
      { name: "vision_title", label: "Vision Title", type: "text", placeholder: "Vision" },
      { name: "vision_text", label: "Vision Text", type: "textarea", rows: 4, placeholder: "Vision statement" },
      { name: "mission_title", label: "Mission Title", type: "text", placeholder: "Mission" },
      { name: "mission_text", label: "Mission Text", type: "textarea", rows: 4, placeholder: "Mission statement" },
      { name: "is_enabled", label: "Show About Section", type: "toggle" },
    ],
    sectionImages: [
      { name: "image", removeName: "remove_image", label: "Primary Image" },
      { name: "secondary_image", removeName: "remove_secondary_image", label: "Secondary Image" },
    ],
    itemSection: "about_highlights",
    itemLabel: "Highlight",
    itemDescription: "Use highlights for statistics, impact metrics, or key differentiators.",
    itemFields: [
      { name: "title", label: "Highlight Title", type: "text", placeholder: "Industry-Relevant Education" },
      { name: "subtitle", label: "Short Label", type: "text", placeholder: "Practical training" },
      { name: "body", label: "Description", type: "textarea", rows: 3, placeholder: "Highlight description" },
      { name: "value", label: "Highlight Value", type: "text", placeholder: "100" },
      { name: "suffix", label: "Value Suffix", type: "text", placeholder: "% Practical" },
      { name: "sort_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_enabled", label: "Show Highlight", type: "toggle" },
    ],
  },
  {
    key: "programs",
    label: "Programs",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
    description: "Manage the programs section heading. Program cards reuse the existing programs table.",
    sectionFields: [
      { name: "badge", label: "Badge", type: "text", placeholder: "Our Programs" },
      { name: "title", label: "Section Title", type: "text", placeholder: "Choose Your Creative Path" },
      { name: "subtitle", label: "Section Subtitle", type: "textarea", rows: 3, placeholder: "Programs section subtitle" },
      { name: "is_enabled", label: "Show Programs Section", type: "toggle" },
    ],
  },
  {
    key: "stats",
    label: "Statistics",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    description: "Control the homepage statistics bar.",
    sectionFields: [
      { name: "is_enabled", label: "Show Stats Bar", type: "toggle" },
    ],
    itemSection: "stats",
    itemLabel: "Stat",
    itemFields: [
      { name: "title", label: "Label", type: "text", placeholder: "Students Enrolled" },
      { name: "value", label: "Value", type: "text", placeholder: "500" },
      { name: "suffix", label: "Suffix", type: "text", placeholder: "+" },
      { name: "sort_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_enabled", label: "Show Stat", type: "toggle" },
    ],
  },
  {
    key: "why_us",
    label: "Why Us",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    description: "Edit the section intro and feature cards shown on the homepage.",
    sectionFields: [
      { name: "badge", label: "Badge", type: "text", placeholder: "Why Mokorotlo" },
      { name: "title", label: "Section Title", type: "text", placeholder: "Built for the Creative Generation" },
      { name: "subtitle", label: "Section Subtitle", type: "textarea", rows: 3, placeholder: "Why us section subtitle" },
      { name: "is_enabled", label: "Show Why Us Section", type: "toggle" },
    ],
    itemSection: "why_us_features",
    itemLabel: "Feature Card",
    itemFields: [
      { name: "title", label: "Card Title", type: "text", placeholder: "Industry Mentors" },
      { name: "body", label: "Description", type: "textarea", rows: 3, placeholder: "Feature description" },
      { name: "icon", label: "Icon", type: "select", options: [
        { value: "mentors", label: "Mentors" },
        { value: "equipment", label: "Equipment" },
        { value: "class", label: "Class" },
        { value: "career", label: "Career" },
        { value: "community", label: "Community" },
        { value: "accredited", label: "Accredited" },
      ] },
      { name: "sort_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_enabled", label: "Show Card", type: "toggle" },
    ],
  },
  {
    key: "intakes",
    label: "Intakes",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    description: "Manage yearly intake windows on the homepage.",
    sectionFields: [
      { name: "badge", label: "Badge", type: "text", placeholder: "Admissions Windows" },
      { name: "title", label: "Section Title", type: "text", placeholder: "Plan Your Next Intake" },
      { name: "subtitle", label: "Section Subtitle", type: "textarea", rows: 3, placeholder: "Intake section subtitle" },
      { name: "button_text", label: "CTA Button Text", type: "text", placeholder: "Apply Now" },
      { name: "button_link", label: "CTA Button Link", type: "text", placeholder: "/apply" },
      { name: "is_enabled", label: "Show Intakes Section", type: "toggle" },
    ],
    itemSection: "intakes",
    itemLabel: "Intake Window",
    itemFields: [
      { name: "title", label: "Intake Name", type: "text", placeholder: "January Intake" },
      { name: "subtitle", label: "Short Subtitle", type: "text", placeholder: "Start the year strong" },
      { name: "body", label: "Description", type: "textarea", rows: 3, placeholder: "Intake description" },
      { name: "month", label: "Month", type: "text", placeholder: "January" },
      { name: "sort_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_enabled", label: "Show Intake", type: "toggle" },
    ],
  },
  {
    key: "expo",
    label: "Expo",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    description: "Edit the Expo narrative, CTA buttons, and highlight cards.",
    sectionFields: [
      { name: "badge", label: "Badge", type: "text", placeholder: "Signature Event" },
      { name: "title", label: "Section Title", type: "text", placeholder: "The Mokorotlo Career Expo" },
      { name: "body", label: "Primary Description", type: "textarea", rows: 4, placeholder: "Main Expo text" },
      { name: "secondary_body", label: "Secondary Description", type: "textarea", rows: 4, placeholder: "Supporting Expo text" },
      { name: "button_text", label: "Primary Button Text", type: "text", placeholder: "Register Your School" },
      { name: "button_link", label: "Primary Button Link", type: "text", placeholder: "/contact" },
      { name: "secondary_button_text", label: "Secondary Button Text", type: "text", placeholder: "Learn More" },
      { name: "secondary_button_link", label: "Secondary Button Link", type: "text", placeholder: "/contact" },
      { name: "is_enabled", label: "Show Expo Section", type: "toggle" },
    ],
    itemSection: "expo_highlights",
    itemLabel: "Highlight Card",
    itemDescription: "Highlight key achievements or stats from previous expos.",
    itemFields: [
      { name: "title", label: "Card Title", type: "text", placeholder: "10+ Schools" },
      { name: "body", label: "Description", type: "textarea", rows: 3, placeholder: "Highlight description" },
      { name: "icon", label: "Icon", type: "select", options: [
        { value: "schools", label: "Schools" },
        { value: "talks", label: "Talks" },
        { value: "networking", label: "Networking" },
        { value: "calendar", label: "Calendar" },
      ] },
      { name: "sort_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_enabled", label: "Show Highlight", type: "toggle" },
    ],
  },
  {
    key: "testimonials",
    label: "Testimonials",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    description: "Manage graduate testimonials, names, qualifications, and profile images.",
    sectionFields: [
      { name: "badge", label: "Badge", type: "text", placeholder: "Graduate Stories" },
      { name: "title", label: "Section Title", type: "text", placeholder: "Hear From Our Graduates" },
      { name: "subtitle", label: "Section Subtitle", type: "textarea", rows: 3, placeholder: "Testimonials subtitle" },
      { name: "is_enabled", label: "Show Testimonials Section", type: "toggle" },
    ],
    itemSection: "testimonials",
    itemLabel: "Testimonial",
    itemFields: [
      { name: "title", label: "Graduate Name", type: "text", placeholder: "Lerato Dlamini" },
      { name: "subtitle", label: "Qualification", type: "text", placeholder: "Information Technology, 2023" },
      { name: "body", label: "Testimonial Message", type: "textarea", rows: 4, placeholder: "Graduate testimonial" },
      { name: "sort_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_enabled", label: "Show Testimonial", type: "toggle" },
    ],
    itemImageField: { name: "image", label: "Graduate Profile Image", required: true },
  },
  {
    key: "admissions",
    label: "Admissions",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>,
    description: "Manage the admissions CTA section and the step-by-step timeline.",
    sectionFields: [
      { name: "badge", label: "Badge", type: "text", placeholder: "Admissions" },
      { name: "title", label: "Section Title", type: "text", placeholder: "Your Journey Starts in 4 Steps" },
      { name: "subtitle", label: "Section Subtitle", type: "textarea", rows: 3, placeholder: "Admissions section subtitle" },
      { name: "button_text", label: "CTA Button Text", type: "text", placeholder: "Apply Now" },
      { name: "button_link", label: "CTA Button Link", type: "text", placeholder: "/apply" },
      { name: "secondary_button_text", label: "Secondary Button Text", type: "text", placeholder: "Contact Admissions" },
      { name: "secondary_button_link", label: "Secondary Button Link", type: "text", placeholder: "/contact" },
      { name: "is_enabled", label: "Show Admissions Section", type: "toggle" },
    ],
    itemSection: "admissions_steps",
    itemLabel: "Step",
    itemFields: [
      { name: "title", label: "Step Title", type: "text", placeholder: "Submit Application" },
      { name: "subtitle", label: "Step Number/Label", type: "text", placeholder: "01" },
      { name: "body", label: "Step Description", type: "textarea", rows: 3, placeholder: "Step details" },
      { name: "sort_order", label: "Display Order", type: "number", min: 0 },
      { name: "is_enabled", label: "Show Step", type: "toggle" },
    ],
  },
  {
    key: "gallery",
    label: "Gallery",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
    special: "gallery",
    description: "The gallery is managed via the main Gallery panel to keep photos and events in sync.",
    sectionFields: [
      { name: "badge", label: "Badge", type: "text", placeholder: "Gallery" },
      { name: "title", label: "Section Title", type: "text", placeholder: "Gallery Highlights" },
      { name: "subtitle", label: "Section Subtitle", type: "textarea", rows: 3, placeholder: "Gallery section subtitle" },
      { name: "is_enabled", label: "Show Gallery Section", type: "toggle" },
    ],
  },
];

function notifyTimer(setter, payload) {
  setter(payload);
  setTimeout(() => setter({ text: "", type: "" }), 3500);
}

function buildSectionForm(definition, section) {
  const base = {};

  definition.sectionFields?.forEach((field) => {
    if (field.type === "toggle") {
      base[field.name] = Boolean(section?.[field.name]);
    } else {
      base[field.name] = section?.[field.name] ?? section?.extra?.[field.name] ?? "";
    }
  });

  return base;
}

function buildEmptyItemForm(definition) {
  const form = {};

  definition.itemFields?.forEach((field) => {
    if (field.type === "toggle") {
      form[field.name] = true;
    } else if (field.type === "number") {
      form[field.name] = 0;
    } else if (field.type === "select") {
      form[field.name] = field.options?.[0]?.value || "";
    } else {
      form[field.name] = "";
    }
  });

  return form;
}

function buildItemForm(definition, item) {
  const form = buildEmptyItemForm(definition);

  definition.itemFields?.forEach((field) => {
    if (field.name in (item?.extra || {})) {
      form[field.name] = item.extra[field.name];
    } else if (field.name === "is_enabled") {
      form[field.name] = Boolean(item?.is_enabled ?? true);
    } else if (item && field.name in item) {
      form[field.name] = item[field.name] ?? form[field.name];
    }
  });

  return form;
}

function ImageDropzone({ label, previewUrl, onChange, onClear, helperText }) {
  return (
    <div className="cms-uploader">
      <span className="cms-uploader__label">{label}</span>
      <label className="cms-uploader__dropzone">
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          onChange={(event) => onChange(event.target.files?.[0] || null)}
          style={{ display: "none" }}
        />
        <span className="cms-uploader__title">Drag an image here or click to browse</span>
        <span className="cms-uploader__hint">{helperText || "JPG, PNG, GIF, or WebP up to 10 MB"}</span>
      </label>
      {previewUrl && (
        <div className="cms-uploader__preview-wrap">
          <img src={previewUrl} alt={label} className="cms-uploader__preview" />
          <button type="button" className="btn btn--outline" onClick={onClear}>Remove Image</button>
        </div>
      )}
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  if (field.type === "textarea") {
    return (
      <label className="admin-form__label">
        {field.label}
        <textarea
          className="admin-form__input admin-form__textarea"
          rows={field.rows || 4}
          value={value}
          placeholder={field.placeholder || ""}
          onChange={(event) => onChange(field.name, event.target.value)}
        />
      </label>
    );
  }

  if (field.type === "toggle") {
    return (
      <label className="cms-switch">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(event) => onChange(field.name, event.target.checked)}
        />
        <span>{field.label}</span>
      </label>
    );
  }

  if (field.type === "select") {
    return (
      <label className="admin-form__label">
        {field.label}
        <select
          className="admin-form__input"
          value={value}
          onChange={(event) => onChange(field.name, event.target.value)}
        >
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <label className="admin-form__label">
      {field.label}
      <input
        className="admin-form__input"
        type={field.type === "number" ? "number" : "text"}
        value={value}
        min={field.min}
        placeholder={field.placeholder || ""}
        onChange={(event) => onChange(field.name, field.type === "number" ? Number(event.target.value || 0) : event.target.value)}
      />
    </label>
  );
}

function LandingSectionEditor({
  definition,
  section,
  items,
  authFetch,
  onNotify,
  onReload,
  onDeleteRequest,
  onOpenGallery,
}) {
  const [sectionForm, setSectionForm] = useState(() => buildSectionForm(definition, section));
  const [sectionFiles, setSectionFiles] = useState({});
  const [sectionPreviews, setSectionPreviews] = useState({});
  const [sectionRemovals, setSectionRemovals] = useState({});
  const [sectionSaving, setSectionSaving] = useState(false);

  const [itemForm, setItemForm] = useState(() => buildEmptyItemForm(definition));
  const [itemFile, setItemFile] = useState(null);
  const [itemPreview, setItemPreview] = useState("");
  const [itemRemoveImage, setItemRemoveImage] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemSaving, setItemSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    setSectionForm(buildSectionForm(definition, section));
    setSectionFiles({});
    setSectionPreviews({});
    setSectionRemovals({});
  }, [definition, section]);

  useEffect(() => {
    setItemForm(buildEmptyItemForm(definition));
    setItemFile(null);
    setItemPreview("");
    setItemRemoveImage(false);
    setEditingItem(null);
  }, [definition]);

  const filteredItems = useMemo(() => {
    return [...(items || [])]
      .filter((item) => {
        if (statusFilter === "enabled" && !item.is_enabled) return false;
        if (statusFilter === "disabled" && item.is_enabled) return false;
        if (!query.trim()) return true;
        const haystack = `${item.title} ${item.subtitle || ""} ${item.body || ""}`.toLowerCase();
        return haystack.includes(query.trim().toLowerCase());
      })
      .sort((left, right) => left.sort_order - right.sort_order || left.id - right.id);
  }, [items, query, statusFilter]);

  const resetItemForm = () => {
    setItemForm(buildEmptyItemForm(definition));
    setItemFile(null);
    setItemPreview("");
    setItemRemoveImage(false);
    setEditingItem(null);
  };

  const handleSectionFieldChange = (name, value) => {
    setSectionForm((current) => ({ ...current, [name]: value }));
  };

  const handleItemFieldChange = (name, value) => {
    setItemForm((current) => ({ ...current, [name]: value }));
  };

  const handleSectionImageChange = (fieldName, file) => {
    setSectionFiles((current) => ({ ...current, [fieldName]: file }));
    setSectionRemovals((current) => ({ ...current, [fieldName]: false }));
    setSectionPreviews((current) => ({
      ...current,
      [fieldName]: file ? URL.createObjectURL(file) : "",
    }));
  };

  const clearSectionImage = (fieldName, removeName) => {
    setSectionFiles((current) => ({ ...current, [fieldName]: null }));
    setSectionPreviews((current) => ({ ...current, [fieldName]: "" }));
    setSectionRemovals((current) => ({ ...current, [fieldName]: true, [removeName]: true }));
  };

  const buildSectionPayload = () => {
    const payload = new FormData();

    definition.sectionFields?.forEach((field) => {
      const value = sectionForm[field.name];
      payload.append(field.name, field.type === "toggle" ? (value ? "1" : "0") : String(value ?? ""));
    });

    definition.sectionImages?.forEach((imageField) => {
      if (sectionFiles[imageField.name]) {
        payload.append(imageField.name, sectionFiles[imageField.name]);
      }
      if (sectionRemovals[imageField.name]) {
        payload.append(imageField.removeName, "1");
      }
    });

    return payload;
  };

  const saveSection = async (event) => {
    event.preventDefault();
    setSectionSaving(true);

    try {
      const res = await authFetch(`/landing-page/sections/${definition.key}`, {
        method: "POST",
        body: buildSectionPayload(),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || "Failed to save section");
      onNotify(payload.message || "Section saved");
      await onReload();
    } catch (error) {
      onNotify(error.message, "error");
    } finally {
      setSectionSaving(false);
    }
  };

  const saveItem = async (event) => {
    event.preventDefault();
    if (!definition.itemSection) return;

    setItemSaving(true);

    try {
      const payload = new FormData();

      definition.itemFields?.forEach((field) => {
        const value = itemForm[field.name];
        payload.append(field.name, field.type === "toggle" ? (value ? "1" : "0") : String(value ?? ""));
      });

      if (definition.itemImageField && itemFile) {
        payload.append(definition.itemImageField.name, itemFile);
      }

      if (itemRemoveImage) {
        payload.append("remove_image", "1");
      }

      const url = editingItem
        ? `/landing-page/items/${definition.itemSection}/${editingItem.id}`
        : `/landing-page/items/${definition.itemSection}`;

      const res = await authFetch(url, {
        method: "POST",
        body: payload,
      });
      const response = await res.json();
      if (!res.ok) throw new Error(response.error || "Failed to save item");

      onNotify(response.message || `${definition.itemLabel} saved`);
      resetItemForm();
      await onReload();
    } catch (error) {
      onNotify(error.message, "error");
    } finally {
      setItemSaving(false);
    }
  };

  const startEditItem = (item) => {
    setEditingItem(item);
    setItemForm(buildItemForm(definition, item));
    setItemPreview(item.image_url || "");
    setItemFile(null);
    setItemRemoveImage(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteItem = async (item) => {
    const res = await authFetch(`/landing-page/items/${definition.itemSection}/${item.id}`, {
      method: "DELETE",
    });
    const payload = await res.json();
    if (!res.ok) throw new Error(payload.error || "Failed to delete item");
    onNotify(payload.message || `${definition.itemLabel} deleted`);
    if (editingItem?.id === item.id) resetItemForm();
    await onReload();
  };

  if (definition.special === "gallery") {
    return (
      <section className="cms-card">
        <header className="cms-panel-header">
          <div className="cms-panel-header__info">
            <h2>Gallery Images</h2>
            <p>{definition.description}</p>
          </div>
        </header>
        <div className="cms-panel-body">
          <div style={{ background: "#f1f5f9", padding: "24px", borderRadius: "12px", border: "1px dashed #cbd5e1" }}>
            <p style={{ fontWeight: "700", color: "#0f172a", marginBottom: "8px" }}>Use the dedicated Gallery manager for uploads, edits, and deletions.</p>
            <p style={{ fontSize: "0.9rem", color: "#64748b", marginBottom: "20px" }}>That manager is still connected to the same backend and gallery table, so updates remain available site-wide.</p>
            <button type="button" className="btn btn--accent" onClick={onOpenGallery}>Open Gallery Manager</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="cms-editor">
      <section className="cms-card">
        <header className="cms-panel-header">
          <div className="cms-panel-header__info">
            <h2>{definition.label} Settings</h2>
            <p>{definition.description}</p>
          </div>
        </header>

        <div className="cms-panel-body">
          <form onSubmit={saveSection} className="admin-form cms-form">
            {definition.sectionFields?.map((field) => (
              <div key={field.name} className={field.type === "textarea" ? "cms-form__full" : ""}>
                <FieldRenderer
                  field={field}
                  value={sectionForm[field.name]}
                  onChange={handleSectionFieldChange}
                />
              </div>
            ))}

            {definition.sectionImages?.map((imageField) => (
              <div key={imageField.name} className="cms-form__full">
                <ImageDropzone
                  label={imageField.label}
                  previewUrl={sectionPreviews[imageField.name] || section?.[`${imageField.name}_url`] || section?.[imageField.name.replace("_image", "_image_url")] || section?.[imageField.name === "image" ? "image_url" : "secondary_image_url"] || ""}
                  onChange={(file) => handleSectionImageChange(imageField.name, file)}
                  onClear={() => clearSectionImage(imageField.name, imageField.removeName)}
                />
              </div>
            ))}

            <div className="admin-form__actions cms-form__full" style={{ marginTop: "12px", borderTop: "1px solid #f1f5f9", paddingTop: "24px" }}>
              <button type="submit" className="btn btn--accent" disabled={sectionSaving}>
                {sectionSaving ? "Saving..." : `Save ${definition.label} Settings`}
              </button>
            </div>
          </form>
        </div>
      </section>

      {definition.itemSection && (
        <>
          <section className="cms-card">
            <header className="cms-panel-header">
              <div className="cms-panel-header__info">
                <h2>{editingItem ? `Edit ${definition.itemLabel}` : `Add New ${definition.itemLabel}`}</h2>
                {definition.itemDescription && <p>{definition.itemDescription}</p>}
              </div>
            </header>

            <div className="cms-panel-body">
              <form onSubmit={saveItem} className="admin-form cms-form">
                {definition.itemFields?.map((field) => (
                  <div key={field.name} className={field.type === "textarea" ? "cms-form__full" : ""}>
                    <FieldRenderer
                      field={field}
                      value={itemForm[field.name]}
                      onChange={handleItemFieldChange}
                    />
                  </div>
                ))}

                {definition.itemImageField && (
                  <div className="cms-form__full">
                    <ImageDropzone
                      label={definition.itemImageField.label}
                      previewUrl={itemPreview}
                      onChange={(file) => {
                        setItemFile(file);
                        setItemRemoveImage(false);
                        setItemPreview(file ? URL.createObjectURL(file) : "");
                      }}
                      onClear={() => {
                        setItemFile(null);
                        setItemPreview("");
                        setItemRemoveImage(true);
                      }}
                    />
                  </div>
                )}

                <div className="admin-form__actions cms-form__full" style={{ marginTop: "12px", borderTop: "1px solid #f1f5f9", paddingTop: "24px" }}>
                  <button type="submit" className="btn btn--accent" disabled={itemSaving}>
                    {itemSaving ? "Saving..." : editingItem ? `Update ${definition.itemLabel}` : `Add ${definition.itemLabel}`}
                  </button>
                  {editingItem && (
                    <button type="button" className="btn btn--outline" onClick={resetItemForm}>
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </section>

          <section className="cms-card">
            <header className="cms-panel-header">
              <div className="cms-panel-header__info">
                <h2>Existing {definition.itemLabel}s</h2>
                <p>Manage and organize your {definition.itemLabel.toLowerCase()}s.</p>
              </div>
              <div className="cms-toolbar" style={{ display: "flex", gap: "12px" }}>
                <input
                  className="admin-form__input"
                  type="text"
                  style={{ minWidth: "200px", margin: 0 }}
                  value={query}
                  placeholder="Search..."
                  onChange={(event) => setQuery(event.target.value)}
                />
                <select
                  className="admin-form__input"
                  style={{ width: "auto", margin: 0 }}
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">All</option>
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            </header>

            <div className="cms-panel-body">
              {filteredItems.length === 0 ? (
                <div className="cms-empty" style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "16px", opacity: 0.5 }}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  <p>No items match the current filters.</p>
                </div>
              ) : (
                <div className="cms-item-grid">
                  {filteredItems.map((item) => (
                    <article key={item.id} className="cms-item-card">
                      {item.image_url && <img src={item.image_url} alt={item.title} className="cms-item-card__image" loading="lazy" />}
                      <div className="cms-item-card__body">
                        <div className="cms-item-card__meta">
                          <span className={`cms-badge ${item.is_enabled ? "cms-badge--success" : "cms-badge--muted"}`}>
                            {item.is_enabled ? "Enabled" : "Disabled"}
                          </span>
                          {item.sort_order !== undefined && (
                            <span className="cms-badge" style={{ background: "#f1f5f9", color: "#475569" }}>
                              Order: {item.sort_order}
                            </span>
                          )}
                        </div>
                        <h3>{item.title}</h3>
                        {item.subtitle && <p className="cms-item-card__subtitle">{item.subtitle}</p>}
                      </div>
                      <div className="cms-item-card__actions">
                        <button type="button" className="btn btn--outline" onClick={() => startEditItem(item)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn--outline"
                          style={{ color: "#ef4444", borderColor: "rgba(239,68,68,0.2)" }}
                          onClick={() => onDeleteRequest({
                            title: `Delete ${definition.itemLabel}`,
                            message: `Are you sure you want to delete "${item.title}"? This action cannot be undone.`,
                            confirmText: "Delete",
                            confirmVariant: "danger",
                            onConfirm: async () => {
                              try {
                                await deleteItem(item);
                              } catch (error) {
                                onNotify(error.message, "error");
                              }
                            },
                          })}
                        >
                          Delete
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default function LandingPagePanel({ authFetch, onOpenGallery, activeSection }) {
  const [data, setData] = useState({ sections: {}, items: {} });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ text: "", type: "" });
  const [confirmState, setConfirmState] = useState(null);

  const notify = (text, type = "success") => notifyTimer(setToast, { text, type });

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/landing-page/admin");
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || "Failed to load landing page data");
      setData({
        sections: payload.sections || {},
        items: payload.items || {},
      });
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeDefinition = SECTION_DEFINITIONS.find((definition) => definition.key === activeSection) || SECTION_DEFINITIONS[0];

  return (
    <div className="cms-content">
      {toast.text && (
        <div className={`admin-toast admin-toast--${toast.type}`} style={{ marginBottom: "24px", position: "relative", top: 0, left: 0, width: "100%" }}>
          {toast.text}
        </div>
      )}

      {loading ? (
        <div className="cms-card">
          <div className="cms-panel-body">
            <p className="cms-empty">Loading {activeDefinition.label} content...</p>
          </div>
        </div>
      ) : (
        <LandingSectionEditor
          definition={activeDefinition}
          section={data.sections[activeDefinition.key]}
          items={data.items[activeDefinition.itemSection] || []}
          authFetch={authFetch}
          onNotify={notify}
          onReload={load}
          onDeleteRequest={setConfirmState}
          onOpenGallery={onOpenGallery}
        />
      )}

      <ConfirmModal state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}
